/**
 * Cross-platform ID generation for objects created on web.
 *
 * The Android app (source of truth) stores every object with a Room
 * `@PrimaryKey(autoGenerate = true) Int` — a SIGNED 32-bit integer. When it
 * syncs to Firestore it writes the document with:
 *   - doc path id  = id.toString()          (a numeric string, e.g. "1751240312")
 *   - inner `id` / `projectId` / `taskId`   = the raw number
 * and reads back via `doc.toObject()` / deletes via `docId.toIntOrNull()`.
 *
 * So anything web creates must carry a NUMERIC id that:
 *   1. fits in a signed 32-bit int  (<= 2,147,483,647) — or toIntOrNull()
 *      returns null on Android and the row silently never lands, and
 *   2. never collides with Android's low sequential ids (1, 2, 3, ...).
 *
 * Strategy: epoch SECONDS, made strictly monotonic within a session.
 *   - Seconds-since-epoch is ~1.75e9 today: comfortably under the 2.147e9
 *     ceiling, and ~6 orders of magnitude above any value Android will ever
 *     auto-increment to, so cross-platform collisions can't happen.
 *   - max(secondsNow, lastIssued + 1) guarantees uniqueness even for rapid
 *     same-second creates. Two web tabs creating in the same second is the
 *     only residual collision, resolved by the same last-write-wins on
 *     `updatedAt` that Android already uses for two offline devices.
 *
 * Ceiling note: epoch-seconds crosses 2^31 in Jan 2038 — the exact ceiling
 * Android's own `Int` PK hits. A shared, documented limit, not a new one.
 *
 * ── FUTURE / multi-user ──────────────────────────────────────────────
 * When the multi-user feature lands, ids move to alphanumeric (UUID-style)
 * strings so independent clients never collide. That swap lives HERE and in
 * the type helpers below — keep all id minting/parsing routed through this
 * module so the migration is a one-file change.
 */

const INT32_MAX = 2147483647;

// Highest id handed out this session. Seeded lazily from existing data so we
// always advance past anything already present (Android rows included).
let lastIssued = 0;

/**
 * Mint a new numeric, Int32-safe, monotonically increasing id.
 *
 * @param {Array<string|number>} existingIds - ids already in play (e.g. the
 *   loaded projects/tasks). Used once to seed the counter so we never reissue
 *   or go backwards relative to data already on the device.
 * @returns {number} a positive integer <= 2,147,483,647
 */
export function generateNumericId(existingIds = []) {
  const maxExisting = existingIds.reduce((max, raw) => {
    const n = Number(raw);
    return Number.isInteger(n) && n > max ? n : max;
  }, 0);

  const seconds = Math.floor(Date.now() / 1000);
  const next = Math.max(seconds, lastIssued + 1, maxExisting + 1);
  lastIssued = next;

  if (next > INT32_MAX) {
    // Past Jan 2038. Android's Int PK can't hold this either — fail loud
    // rather than silently writing a row that never syncs.
    throw new Error(
      `Generated id ${next} exceeds Int32 max (${INT32_MAX}); ` +
        `id scheme must move to strings before this point.`
    );
  }
  return next;
}

/** The Firestore document path id for an object id (always a numeric string). */
export function toDocId(id) {
  return String(id);
}

/**
 * Coerce an id to the value type used INSIDE Firestore documents for reference
 * fields (projectId, taskId, ...). Android stores these as numbers, so we must
 * too. Accepts the numeric strings web carries internally (doc.id is a string).
 */
export function toRefValue(id) {
  const n = Number(id);
  return Number.isNaN(n) ? id : n;
}
